import * as Api from '@ViewModels';
import * as React from 'react';
import { toast } from 'sonner';
import { submitRegEdCompliance } from './submitRegEdCompliance';
import { IRegEdSubmissionView, RegEdComplianceContentType } from './types';

interface IRevisionOwed {
	approvalRequestId: string;
	/** Which step is still owed: the repoint, or only the compliance document. */
	needsFiling: boolean;
	submission: IRegEdSubmissionView;
}

interface IArgs {
	/** Passed in rather than pulled from a hook: the email host is MobX-injected and already holds one. */
	userSession: Api.UserSessionContext;
	parentId: string | undefined;
	parentType: RegEdComplianceContentType;
	/** Rebuilt per call: the hosts derive it from a viewmodel that can change under them. */
	getImpersonationContext(): Api.IImpersonationContext | undefined;
	/** Renders the compliance document for the parent's open round. */
	regenerateCompliancePdf(impersonationContext: Api.IImpersonationContext | undefined): Promise<unknown>;
	/**
	 * The host's own follow-up once a revision is complete — reloading its viewmodel, or leaving a
	 * screen that is now showing a state the revision has left. Failures here are not the advisor's to
	 * act on, so they must not be reported as an unfiled submission.
	 */
	onRevisionFiled?(): void | Promise<void>;
	logApiError(action: string, error?: Api.IOperationResultNoValue): void;
}

/**
 * The filing half of a revision: repoint the RegEd submission onto the raised round, then render the
 * document that actually files it.
 *
 * Resumable, because either call can fail on its own — and they are not interchangeable. The repoint
 * rewrites the submission's `approvalRequestId` to the new round, so replaying it looks the prior
 * round up by an id nothing carries any more and fails ("No RegEd submission found for prior approval
 * request"). A retry has to resume at whichever step failed, which is what `needsFiling` records.
 *
 * Lives here rather than in each host because the protocol below — what the lead-time prompt owns,
 * what a landed repoint forbids, and which of the two steps a failure leaves owed — has to hold
 * identically for email, blog and social, and a copy per host is a copy that drifts.
 */
export const useRegEdRevisionFiling = ({
	userSession,
	parentId,
	parentType,
	getImpersonationContext,
	regenerateCompliancePdf,
	onRevisionFiled,
	logApiError,
}: IArgs) => {
	const [revisionOwed, setRevisionOwed] = React.useState<IRevisionOwed | null>(null);
	const [isRetryingRevision, setIsRetryingRevision] = React.useState(false);

	/** The half that runs once the repoint has landed, whenever that turns out to be. */
	const finishRevisionFiling = async (approvalRequestId: string, submission: IRegEdSubmissionView) => {
		try {
			await regenerateCompliancePdf(getImpersonationContext());
		} catch {
			// The round is open with nothing filed, which no status field reveals.
			setRevisionOwed({ approvalRequestId, needsFiling: false, submission });
			return false;
		}
		setRevisionOwed(null);
		toast.success('Sent back for compliance review.');
		// Outside the guard above: a failure here leaves the filing done, so reporting it as an unfiled
		// submission would be a lie the advisor cannot act on.
		try {
			await onRevisionFiled?.();
		} catch (error) {
			logApiError(`RegEd${parentType}Revise-Complete-Error`, Api.asApiError(error));
		}
		return true;
	};

	const runRevisionFiling = async (
		approvalRequestId: string,
		submission: IRegEdSubmissionView,
		needsFiling: boolean
	) => {
		if (!needsFiling) {
			return finishRevisionFiling(approvalRequestId, submission);
		}
		// The lead-time prompt can take ownership of the filing and succeed after this call has already
		// returned false, so the document hangs off onFiled rather than the returned boolean.
		let deferredToOverride = false;
		let repointLanded = false;
		let completed = false;
		const filed = await submitRegEdCompliance({
			action: 'sent',
			approvalRequestId,
			impersonationContext: getImpersonationContext(),
			logApiError,
			metadata: submission?.metadata,
			onDeferredToLeadTimeOverride: () => {
				deferredToOverride = true;
			},
			onLeadTimeOverrideDismissed: () => {
				setRevisionOwed({ approvalRequestId, needsFiling: true, submission });
			},
			onFiled: async () => {
				repointLanded = true;
				completed = await finishRevisionFiling(approvalRequestId, submission);
				return completed;
			},
			parentId,
			parentType,
			revisesApprovalRequestId: submission?.approvalRequestId ?? null,
			userSession,
		});
		// Only when the repoint never landed. Once onFiled has run it has, and finishRevisionFiling has
		// already recorded what is still owed — overwriting here would send the retry back through a
		// repoint that cannot be replayed.
		if (!filed && !deferredToOverride && !repointLanded) {
			setRevisionOwed({ approvalRequestId, needsFiling: true, submission });
		}
		// Both have to hold: the filing lands before the document is rendered, and a caller that reads
		// only the filing would treat an unrendered document as a completed revision.
		return filed && completed;
	};

	const onRetryRevisionClicked = async () => {
		if (!revisionOwed) {
			return;
		}
		// The filing step is a plain call with no mutation state of its own, so the button needs this to
		// stay held while it runs.
		setIsRetryingRevision(true);
		try {
			await runRevisionFiling(revisionOwed.approvalRequestId, revisionOwed.submission, revisionOwed.needsFiling);
		} finally {
			setIsRetryingRevision(false);
		}
	};

	return { isRetryingRevision, onRetryRevisionClicked, revisionOwed, runRevisionFiling } as const;
};
